<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useBillingAccess } from 'dashboard/composables/useBillingAccess';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  bypass: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();
const { isAdmin } = useAdmin();
const { displayStatus, isBillingRestricted } = useBillingAccess();

const shouldBlock = computed(() => !props.bypass && isBillingRestricted.value);

const titleKey = computed(() => {
  const status = displayStatus.value;
  if (status === 'frozen') {
    return 'GENERAL_SETTINGS.BILLING_USAGE_PAGE.FROZEN_TITLE';
  }
  if (status === 'trial_ended') {
    return 'GENERAL_SETTINGS.BILLING_USAGE_PAGE.BADGE_TRIAL_ENDED';
  }
  return 'GENERAL_SETTINGS.BILLING_USAGE_PAGE.BADGE_PENDING_PAYMENT';
});

const descriptionKey = computed(() => {
  const status = displayStatus.value;
  if (status === 'frozen') {
    return 'GENERAL_SETTINGS.BILLING_USAGE_PAGE.FROZEN_DESCRIPTION';
  }
  if (status === 'trial_ended') {
    return 'GENERAL_SETTINGS.BILLING_USAGE_PAGE.TRIAL_ENDED_DESCRIPTION';
  }
  return 'GENERAL_SETTINGS.BILLING_USAGE_PAGE.PENDING_PAYMENT_DESCRIPTION';
});

const routeToBilling = () => {
  router.push({
    name: 'general_settings_billing',
    params: { accountId: accountId.value },
  });
};
</script>

<template>
  <div v-if="shouldBlock" class="mx-auto h-full pt-[clamp(3rem,15vh,12rem)]">
    <div
      class="mx-auto flex max-w-md flex-col gap-4 rounded-xl bg-n-solid-1 px-8 py-6 shadow-lg outline outline-1 outline-n-container"
    >
      <div class="flex items-center gap-2">
        <span
          class="flex size-6 items-center justify-center rounded-full bg-n-solid-blue"
        >
          <Icon
            class="shrink-0 text-n-brand size-[14px]"
            icon="i-lucide-lock-keyhole"
          />
        </span>
        <span class="text-base font-medium text-n-slate-12">
          {{ t(titleKey) }}
        </span>
      </div>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t(descriptionKey) }}
      </p>
      <p v-if="!isAdmin" class="mb-0 text-sm text-n-slate-11">
        {{ t('GENERAL_SETTINGS.LIMIT_MESSAGES.NON_ADMIN') }}
      </p>
      <Button
        v-if="isAdmin"
        :label="t('GENERAL_SETTINGS.OPEN_BILLING')"
        icon="i-lucide-credit-card"
        @click="routeToBilling"
      />
    </div>
  </div>
  <div v-else class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
    <slot />
  </div>
</template>
